import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F5ECE1]">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-[auto,1fr] items-center gap-4 mb-8">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div className="flex justify-center">
            <img 
              src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
              alt="Framemyhome"
              className="h-10"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: October 26, 2025</p>

          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-600 mb-4">
              At Frame My Home, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information when you use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
            <div className="space-y-4 text-gray-600">
              <p>We collect the following types of information:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Contact information (name, email address, shipping address)</li>
                <li>Order details and preferences</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Property images and addresses submitted for framing</li>
                <li>Usage data through Google Analytics</li>
                <li>Session interaction data for analytics purposes (see Session Recording section below)</li>
              </ul>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <div className="space-y-4 text-gray-600">
              <p>Your information is used for:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Processing and fulfilling your orders</li>
                <li>Sending order confirmations and shipping updates</li>
                <li>Improving our services and user experience</li>
                <li>Analyzing website traffic and usage patterns through analytics and session recording</li>
                <li>Identifying and resolving technical issues</li>
                <li>Communicating important service updates</li>
              </ul>
            </div>
          </section>

          {/* Data Protection */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Protection</h2>
            <div className="space-y-4 text-gray-600">
              <p>We implement various security measures to protect your data:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Secure payment processing through Stripe</li>
                <li>Encrypted data transmission</li>
                <li>Limited access to personal information</li>
                <li>Regular security assessments and updates</li>
              </ul>
            </div>
          </section>

          {/* Website Analytics */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Website Analytics</h2>
            <div className="space-y-4 text-gray-600">
              <p>
                We use standard website analytics (including Google Analytics) to understand how visitors use our site and improve your experience. These tools collect general usage data like pages viewed, device type, and navigation patterns—similar to virtually all modern websites. This helps us identify technical issues, improve site usability, and make your experience better.
              </p>
              <p>
                We do not collect or have access to sensitive information including credit card numbers, payment details, or passwords. All payment processing is handled securely by Stripe's encrypted checkout system, completely separate from our website analytics.
              </p>
              <p>
                You can opt-out of Google Analytics tracking by using the Google Analytics Opt-out Browser Add-on.
              </p>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <div className="space-y-4 text-gray-600">
              <p>We work with trusted third-party services:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Stripe for payment processing</li>
                <li>Shipping carriers for order delivery</li>
                <li>Google Analytics for website analytics</li>
              </ul>
              <p>
                These services have their own privacy policies and handling practices. We recommend reviewing their privacy policies for more information.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <div className="space-y-4 text-gray-600">
              <p>
                If you have any questions about our Privacy Policy, please contact us:
              </p>
              <p>
                Email: <a href="mailto:support@framemyhome.ai" className="text-blue-600 hover:text-blue-800">support@framemyhome.ai</a>
              </p>
            </div>
          </section>

          {/* Updates to Privacy Policy */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Updates to Privacy Policy</h2>
            <div className="space-y-4 text-gray-600">
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. Continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 